import { useCallback } from 'react';
import { useImageUploadMutation } from '../ImageLibrary/useImageUploadMutation';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectContentId } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { parseURLFromHTML } from '../ImageLibrary/helpers';

export type ImageUploadCallbackPerImage = (imageLink: string) => void;

const urlToFile = async (url: string, filename?: string): Promise<File> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    const blob = await response.blob();
    const file = new File([blob], filename || `image-${Date.now()}.${blob.type.split('/')[1] || 'jpg'}`, {
      type: blob.type || 'image/jpeg',
    });
    return file;
  } catch (error) {
    throw new Error(`Failed to convert URL to file: ${error instanceof Error ? error.message : String(error)}`);
  }
};

export function useCanvasImageUploadHandler() {
  const contentId = useAppSelector(selectContentId);
  const { mutate: uploadImage, getUploadedImages } = useImageUploadMutation();

  const handleImageUpload = useCallback(
    (files: File[], callbackPerImage?: ImageUploadCallbackPerImage): Promise<string[] | undefined> => {
      if (files.length === 0) {
        return Promise.resolve(undefined);
      }

      return new Promise((resolve) => {
        uploadImage(
          { contentId, images: files },
          {
            onSuccess: (data) => {
              const uploadedImages = getUploadedImages(data);
              const imageLinks = uploadedImages.map((image) => image.link);

              if (callbackPerImage) {
                imageLinks.forEach(callbackPerImage);
              }

              resolve(imageLinks);
            },
            onError: () => {
              resolve(undefined);
            },
          }
        );
      });
    },
    [contentId, uploadImage, getUploadedImages]
  );

  const handleImageUrlUpload = useCallback(
    async (url: string, callbackPerImage?: ImageUploadCallbackPerImage): Promise<string | undefined> => {
      try {
        const urlObj = new URL(url);
        if (urlObj.origin === globalThis.env.VITE_PUBLIC_CDN_URL) {
          callbackPerImage?.(url);
          return url;
        }

        const file = await urlToFile(url);
        const [link] = (await handleImageUpload([file], callbackPerImage)) ?? [];
        return link;
      } catch {
        return undefined;
      }
    },
    [handleImageUpload]
  );

  const handleImageHtmlUpload = useCallback(
    (html: string, callbackPerImage?: ImageUploadCallbackPerImage): Promise<string | undefined> => {
      const url = parseURLFromHTML(html);
      if (!url) {
        return Promise.resolve(undefined);
      }
      return handleImageUrlUpload(url, callbackPerImage);
    },
    [handleImageUrlUpload]
  );

  return {
    handleImageUpload,
    handleImageUrlUpload,
    handleImageHtmlUpload,
  };
}
